import { z } from 'zod';
export { u as useGetLangProperties } from './client-12s-BAwMpPO9.js';
import { thumbHashToDataURL } from 'thumbhash';
import { Children, isValidElement } from 'react';
import { createSafeActionClient } from 'next-safe-action';
import { _ as _extends } from './cc-he3fHS3P.js';
export * from './utils.js';

const consentSettingsOptions = [
    "denied",
    "granted"
];
const consentSettingsSchema = z.object({
    ad_storage: z.enum(consentSettingsOptions),
    ad_personalization: z.enum(consentSettingsOptions),
    ad_user_data: z.enum(consentSettingsOptions),
    analytics_storage: z.enum(consentSettingsOptions),
    functionality_storage: z.enum(consentSettingsOptions),
    personalization_storage: z.enum(consentSettingsOptions),
    security_storage: z.enum(consentSettingsOptions)
});
const cookieOptions = {
    maxAge: 60 * 60 * 24 * 365
};
const defaultConsentSettings = {
    ad_storage: "denied",
    ad_personalization: "denied",
    ad_user_data: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "denied"
};
const acceptAllConsentSettings = {
    ad_storage: "granted",
    ad_personalization: "granted",
    ad_user_data: "granted",
    analytics_storage: "granted",
    functionality_storage: "granted",
    personalization_storage: "granted",
    security_storage: "granted"
};
const denyAllAdditionalConsentSettings = {
    ad_storage: "denied",
    ad_personalization: "denied",
    ad_user_data: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "denied"
};
const consentTypes = [
    {
        name: "functionality_storage",
        description: "Enables storage that supports the functionality of the website or app e.g. language settings."
    },
    {
        name: "personalization_storage",
        description: "Enables storage related to personalization e.g. video recommendations."
    },
    {
        name: "security_storage",
        description: "Enables storage related to security such as authentication functionality, fraud prevention, and other user protection."
    },
    {
        name: "analytics_storage",
        description: "Enables storage (such as cookies) related to analytics e.g. visit duration."
    },
    {
        name: "ad_storage",
        description: "Enables storage (such as cookies) related to advertising."
    },
    {
        name: "ad_personalization",
        description: "Sets consent for personalized advertising."
    },
    {
        name: "ad_user_data",
        description: "Sets consent for sending user data to partners for advertising purposes."
    }
];

function isErrorWithMessage(error) {
    return typeof error === "object" && error !== null && "message" in error && typeof error.message === "string";
}
function toErrorWithMessage(maybeError) {
    if (isErrorWithMessage(maybeError)) return maybeError;
    try {
        return new Error(JSON.stringify(maybeError));
    } catch (unused) {
        // fallback in case there's an error stringifying the maybeError
        // like with circular references for example.
        return new Error(String(maybeError));
    }
}
function getErrorMessage(error) {
    return toErrorWithMessage(error).message;
}
function isErrorsWithMessage(errors) {
    return Array.isArray(errors) && errors.every((error)=>isErrorWithMessage(error));
}
function toErrorsWithMessage(maybeErrors) {
    if (isErrorsWithMessage(maybeErrors)) return maybeErrors;
    try {
        if (Array.isArray(maybeErrors)) {
            const errors = maybeErrors.map((maybeError)=>new Error(JSON.stringify(maybeError)));
            return errors;
        } else {
            return [
                toErrorWithMessage(maybeErrors)
            ];
        }
    } catch (unused) {
        // fallback in case there's an error stringifying the maybeError
        // like with circular references for example.
        return [
            new Error(String(maybeErrors))
        ];
    }
}
function getErrorsMessage(error) {
    var _toErrorsWithMessage_;
    return (_toErrorsWithMessage_ = toErrorsWithMessage(error)[0]) == null ? void 0 : _toErrorsWithMessage_.message;
}

function onlyUnique(value, index, self) {
    return self.indexOf(value) === index;
}

const getCountryFlagEmoji = (countryCode)=>{
    const codePoints = countryCode.toUpperCase().split("").map((char)=>127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
};

function getIETFLanguageTagFromlocaleTag(locale) {
    return locale.baseName;
}
function getLocaleObjectFromIETFLanguageTag(lang) {
    return new Intl.Locale(lang);
}
function getLangProperties(lang) {
    try {
        const canonicalLocales = Intl.getCanonicalLocales(lang);
        if (!Array.isArray(canonicalLocales)) {
            throw TypeError("Expected a single canonical locale");
        }
    } catch (error) {
        console.error(error, lang);
    }
    const locale = new Intl.Locale(lang);
    const country = locale.region;
    const language = locale.language.toLocaleUpperCase();
    return {
        country,
        language
    };
}

function base64ToUint8Array(base64) {
    const decodedString = atob(base64);
    const arrayBuffer = new Uint8Array(decodedString.length);
    for(let i = 0; i < decodedString.length; i++){
        arrayBuffer[i] = decodedString.charCodeAt(i);
    }
    return arrayBuffer;
}
function thumbhashDataURL(thumbhash) {
    const dataURL = thumbHashToDataURL(base64ToUint8Array(thumbhash));
    return dataURL;
}
function onLoad(target) {
    target.dataset.status = "loaded";
}
function onLoading(target) {
    target.dataset.status = "loading";
}

function getValidChildren(children) {
    return Children.toArray(children).filter((child)=>isValidElement(child));
}

// import { formatErrorMessage, isShopifyError } from "./type-guards";
const actionClientReturnSchema = z.object({
    message: z.string(),
    parsedInput: z.any().optional(),
    prevData: z.any().optional(),
    success: z.boolean()
});
const actionClient = createSafeActionClient({
    defineMetadataSchema: ()=>{
        return z.object({
            actionName: z.string()
        });
    }
});

const breakpointSizes = [
    "sm",
    "md",
    "lg",
    "xl",
    "2xl"
];
const getBreakpointPropertyValue = (breakpointSize)=>Number.parseInt(document.documentElement.style.getPropertyValue(`--breakpoint-${String(breakpointSize)}`));
function getBreakpointCSSUnitValueMax(breakpointSize) {
    const breakpointIndex = breakpointSizes.indexOf(breakpointSize);
    const nextBreakpointSize = breakpointSizes[breakpointIndex + 1];
    if (!nextBreakpointSize) {
        return null;
    }
    const value = getBreakpointPropertyValue(nextBreakpointSize);
    if (!value) {
        return null;
    }
    return new CSSUnitValue(value, "px");
}
function getBreakpointCSSUnitValueMin(breakpointSize) {
    const value = getBreakpointPropertyValue(breakpointSize);
    if (!value) {
        return null;
    }
    return new CSSUnitValue(value, "px");
}
const breakpoints = typeof window !== "undefined" ? breakpointSizes.map((breakpointSize)=>({
        [breakpointSize]: {
            min: getBreakpointCSSUnitValueMin(breakpointSize),
            max: getBreakpointCSSUnitValueMax(breakpointSize)
        }
    })).reduce((acc, cur)=>_extends({}, acc, cur), {}) : undefined;

function isObject(object) {
    return typeof object === "object" && object !== null && !Array.isArray(object);
}
function isShopifyError(error) {
    if (!isObject(error)) return false;
    if (error instanceof Error) return true;
    return findError(error);
}
function findError(error) {
    if (Object.prototype.toString.call(error) === "[object Error]") {
        return true;
    }
    const prototype = Object.getPrototypeOf(error);
    return prototype === null ? false : findError(prototype);
}
function formatErrorMessage(err) {
    return JSON.stringify(err, Object.getOwnPropertyNames(err));
}

export { acceptAllConsentSettings, actionClient, actionClientReturnSchema, base64ToUint8Array, breakpointSizes, breakpoints, consentSettingsOptions, consentSettingsSchema, consentTypes, cookieOptions, defaultConsentSettings, denyAllAdditionalConsentSettings, findError, formatErrorMessage, getBreakpointCSSUnitValueMax, getBreakpointCSSUnitValueMin, getBreakpointPropertyValue, getCountryFlagEmoji, getErrorMessage, getErrorsMessage, getIETFLanguageTagFromlocaleTag, getLangProperties, getLocaleObjectFromIETFLanguageTag, getValidChildren, isErrorWithMessage, isErrorsWithMessage, isObject, isShopifyError, onLoad, onLoading, onlyUnique, thumbhashDataURL, toErrorWithMessage, toErrorsWithMessage };
